/**
 * EcoVadis Module - Global ESG Assessment Platform
 * 4 themes: Environment, Labor & Human Rights, Ethics, Sustainable Procurement
 * Question-based assessment with automatic scoring and medal calculation
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Save, RefreshCw, Download, FileText, Award, Sparkles,
  Leaf, Users, Shield, ShoppingCart, CheckCircle, XCircle, MinusCircle
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const EcoVadisModule = ({ study }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [themes, setThemes] = useState([]);
  const [activeTheme, setActiveTheme] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [score, setScore] = useState(null);
  const [assessmentPeriod, setAssessmentPeriod] = useState('');

  useEffect(() => {
    if (study?.id) {
      fetchData();
    }
  }, [study?.id]);

  const getCurrentFY = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const startYear = month >= 3 ? year : year - 1;
    return `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
  };

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [themesRes, scoreRes, metadataRes] = await Promise.all([
        axios.get(`${API}/api/ecovadis/${study.id}/themes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/ecovadis/${study.id}/score`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/api/metadata/${study.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { metadata: {} } }))
      ]);

      const meta = metadataRes.data?.metadata || metadataRes.data || {};
      setAssessmentPeriod(meta.financial_year || getCurrentFY());

      setThemes(themesRes.data.themes || []);

      // Initialize answers from saved data
      const answerMap = {};
      themesRes.data.themes?.forEach(theme => {
        theme.criteria?.forEach(criterion => {
          criterion.questions?.forEach(question => {
            if (question.answer !== undefined) {
              answerMap[question.id] = {
                answer: question.answer,
                evidence: question.evidence || ''
              };
            }
          });
        });
      });
      setAnswers(answerMap);

      if (scoreRes.data) {
        setScore(scoreRes.data);
      }

      // Set first theme as active
      if (themesRes.data.themes?.length > 0) {
        setActiveTheme(themesRes.data.themes[0].id);
      }
    } catch (error) {
      console.error('Error fetching EcoVadis data:', error);
      showError('Failed to load EcoVadis data');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, field, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
  };

  const saveThemeData = async (themeId) => {
    setSaving(true);
    const token = localStorage.getItem('token');

    // Collect all answers across all themes
    const allAnswers = [];
    themes.forEach(theme => {
      theme.criteria.forEach(criterion => {
        criterion.questions.forEach(question => {
          const ans = answers[question.id];
          if (ans && ans.answer) {
            allAnswers.push({
              theme_id: theme.id,
              criteria_id: criterion.criteria_id,
              question: question.question,
              answer: ans.answer,
              evidence: ans.evidence || null
            });
          }
        });
      });
    });

    try {
      await axios.post(
        `${API}/api/ecovadis/${study.id}/data`,
        { answers: allAnswers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Data saved successfully!');

      // Refresh score
      const scoreRes = await axios.get(`${API}/api/ecovadis/${study.id}/score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScore(scoreRes.data);
    } catch (error) {
      console.error('Error saving data:', error);
      showError('Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  const generateAIPoweredReport = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      showInfo('Generating AI-powered EcoVadis assessment report... This may take a few minutes.');

      // Generate narratives (EcoVadis can have optional narratives)
      await axios.post(
        `${API}/api/ecovadis/${study.id}/generate-narratives`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {
        // Narratives are optional for EcoVadis, continue if it fails
        console.log('Narratives generation skipped or failed');
      });

      // Then download the PDF
      const response = await axios.get(
        `${API}/api/ecovadis/${study.id}/export/pdf?token=${token}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `EcoVadis_Report_${study.name || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('AI-powered report generated and downloaded successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      showError(error.response?.data?.detail || 'Failed to generate AI-powered report');
    } finally {
      setSaving(false);
    }
  };

  const exportPDF = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${API}/api/ecovadis/${study.id}/export/pdf?assessment_period=${encodeURIComponent(assessmentPeriod)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ecovadis_assessment_${study.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showError('Failed to export PDF');
    }
  };

  const exportExcel = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${API}/api/ecovadis/${study.id}/export/excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ecovadis_assessment_${study.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showError('Failed to export Excel');
    }
  };

  const getThemeIcon = (themeId) => {
    const icons = {
      environment: Leaf,
      labor_hr: Users,
      ethics: Shield,
      procurement: ShoppingCart
    };
    return icons[themeId] || Leaf;
  };

  const getMedalColor = (medal) => {
    const colors = {
      'Platinum': 'text-gray-400',
      'Gold': 'text-yellow-500',
      'Silver': 'text-gray-300',
      'Bronze': 'text-orange-600'
    };
    return colors[medal] || 'text-gray-500';
  };

  const getMedalBgColor = (medal) => {
    const colors = {
      'Platinum': 'bg-gray-100',
      'Gold': 'bg-yellow-50',
      'Silver': 'bg-gray-50',
      'Bronze': 'bg-orange-50'
    };
    return colors[medal] || 'bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-500" />
        <span className="ml-3 text-lg">Loading EcoVadis Module...</span>
      </div>
    );
  }

  const currentTheme = themes.find(t => t.id === activeTheme);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-amber-600 rounded-lg shadow-lg p-6 mb-6 text-white">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">
              EcoVadis ESG Assessment
            </h1>
            <p className="text-amber-100">
              Global ESG Performance Assessment • {study?.study_name}
            </p>

            {/* Score Display */}
            {score && (
              <div className="mt-4 flex items-center gap-6">
                <div>
                  <div className="text-5xl font-bold text-indigo-600">
                    {score.overall_score}/100
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Overall Score</div>
                </div>

                {score.medal && (
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${getMedalBgColor(score.medal)}`}>
                    <Award className={`w-8 h-8 ${getMedalColor(score.medal)}`} />
                    <div>
                      <div className={`text-xl font-bold ${getMedalColor(score.medal)}`}>
                        {score.medal} Medal
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={exportExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              Excel
            </button>
            <button
              onClick={generateAIPoweredReport}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              AI-Powered Report
            </button>
          </div>
        </div>

        {/* Theme Scores */}
        {score && score.theme_scores && (
          <div className="mt-4 grid grid-cols-4 gap-4">
            {themes.map(theme => {
              const themeScore = score.theme_scores[theme.id];
              const scoreValue = typeof themeScore === 'object' ? themeScore.score : (themeScore || 0);
              return (
                <div key={theme.id} className="bg-gray-50 rounded-lg p-3">
                  <div className="text-2xl font-bold" style={{ color: theme.color }}>
                    {scoreValue}
                  </div>
                  <div className="text-sm text-gray-600">{theme.title}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex gap-6">
        {/* Sidebar - Themes */}
        <div className="w-80 bg-white rounded-lg shadow-md p-4">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">4 Assessment Themes</h2>
          <div className="space-y-2">
            {themes.map((theme) => {
              const Icon = getThemeIcon(theme.id);
              return (
                <button
                  key={theme.id}
                  onClick={() => setActiveTheme(theme.id)}
                  className={`w-full text-left p-3 rounded-lg transition-all flex items-center gap-3 ${
                    activeTheme === theme.id
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={
                    activeTheme === theme.id
                      ? { backgroundColor: theme.color }
                      : {}
                  }
                >
                  <Icon className="w-5 h-5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{theme.title}</div>
                    {score && score.theme_scores && (
                      <div className="text-xs opacity-80">
                        Score: {typeof score.theme_scores[theme.id] === 'object' ? score.theme_scores[theme.id].score : (score.theme_scores[theme.id] || 0)}/100
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content - Active Theme */}
        <div className="flex-1 bg-white rounded-lg shadow-md p-6">
          {currentTheme && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                  <div
                    className="w-3 h-8 rounded"
                    style={{ backgroundColor: currentTheme.color }}
                  />
                  {currentTheme.title}
                </h2>
                <div className="text-sm text-gray-600">
                  Weight: {currentTheme.weight}% of overall score
                </div>
              </div>

              {/* Criteria and Questions */}
              <div className="space-y-8">
                {currentTheme.criteria.map((criterion) => (
                  <div key={criterion.criteria_id} className="border-b pb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      {criterion.name}
                    </h3>

                    <div className="space-y-4">
                      {criterion.questions.map((question) => (
                        <div key={question.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="mb-3">
                            <p className="font-medium text-gray-800">{question.question}</p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-gray-700 mb-2">
                                Answer
                              </label>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleAnswerChange(question.id, 'answer', 'yes')}
                                  className={`flex-1 px-3 py-2 rounded-lg border transition-all ${
                                    answers[question.id]?.answer === 'yes'
                                      ? 'bg-green-500 text-white border-green-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                                  }`}
                                >
                                  <CheckCircle className="w-4 h-4 mx-auto" />
                                  <span className="text-xs block mt-1">Yes</span>
                                </button>

                                <button
                                  onClick={() => handleAnswerChange(question.id, 'answer', 'partial')}
                                  className={`flex-1 px-3 py-2 rounded-lg border transition-all ${
                                    answers[question.id]?.answer === 'partial'
                                      ? 'bg-yellow-500 text-white border-yellow-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-yellow-400'
                                  }`}
                                >
                                  <MinusCircle className="w-4 h-4 mx-auto" />
                                  <span className="text-xs block mt-1">Partial</span>
                                </button>

                                <button
                                  onClick={() => handleAnswerChange(question.id, 'answer', 'no')}
                                  className={`flex-1 px-3 py-2 rounded-lg border transition-all ${
                                    answers[question.id]?.answer === 'no' || !answers[question.id]?.answer
                                      ? 'bg-red-500 text-white border-red-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'
                                  }`}
                                >
                                  <XCircle className="w-4 h-4 mx-auto" />
                                  <span className="text-xs block mt-1">No</span>
                                </button>

                                <button
                                  onClick={() => handleAnswerChange(question.id, 'answer', 'na')}
                                  className={`flex-1 px-3 py-2 rounded-lg border transition-all ${
                                    answers[question.id]?.answer === 'na'
                                      ? 'bg-gray-500 text-white border-gray-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                                  }`}
                                >
                                  <span className="text-sm block">N/A</span>
                                </button>
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm text-gray-700 mb-2">
                                Evidence / Notes
                              </label>
                              <input
                                type="text"
                                value={answers[question.id]?.evidence || ''}
                                onChange={(e) => handleAnswerChange(question.id, 'evidence', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                placeholder="Supporting evidence or documentation"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-3 border-t pt-6">
                <button
                  onClick={() => saveThemeData(currentTheme.id)}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save & Calculate Score
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EcoVadisModule;
